import React, { useEffect, useMemo, useRef, useState } from "react";
import env from "../../../../config/env";
import { uploadDocumentHeaders } from "../../../../utils/uploadDocumentHeaders";
import { Drawer, Button, Input, InputNumber, DatePicker, Select, Radio, Upload, message } from "antd";
import { CloseOutlined, InboxOutlined, AudioOutlined, CameraOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DefaultProductImage from "../../../../assets/images/no-image.svg";

import {
  normaliseConfig,
  buildInitialValues,
  fieldsBySection,
} from "./customAttributes/normalize";
import { computePricing } from "./customAttributes/pricing";
import {
  isFieldValid,
  isSectionComplete,
  sectionHasRequired,
  isAllValid,
} from "./customAttributes/validation";
import "./customAttributes/styles.css";
import useAuthStore from "../../../../stores/useAuthStore";
import { getCurrencySymbol } from "../../../../utils/tillHelpers";

const { TextArea } = Input;

const formatINR = (n) => getCurrencySymbol(useAuthStore.getState().tillData) + Math.round(Number(n) || 0).toLocaleString("en-IN");

const CustomAttributesDrawer = ({
  open,
  visible,
  onCancel,
  onSubmit,
  selectedProduct,
}) => {
  const isOpen = open ?? visible ?? false;
  const cfg = useMemo(
    () => normaliseConfig(selectedProduct?.customAttributes),
    [selectedProduct]
  );
  const basePrice = Number(selectedProduct?.sunitprice) || 0;

  // Weight-collection mode: product needs manual qty + has custom attributes
  const isManualQtyMode = selectedProduct?.isManualQty === "Y" || selectedProduct?.isManualQty === true;
  const isDecimalQty = selectedProduct?.isDecimalQty ?? false;
  const weightPrecision = isDecimalQty ? (selectedProduct?.isQtyDesimal ?? 2) : 0;
  const weightUomName = selectedProduct?.uomData?.[0]?.name || "kg";

  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [recordingField, setRecordingField] = useState(null);
  const [weightValue, setWeightValue] = useState(null);
  const [weightTouched, setWeightTouched] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recStartRef = useRef(0);

  // Weight/qty pre-set from the weight popup (before the drawer opened).
  const productQty = Number(selectedProduct?.qty) || 1;

  // Reset state whenever the drawer opens for a new product.
  useEffect(() => {
    if (!isOpen || !selectedProduct) return;
    const initial = buildInitialValues(cfg);
    // If the product already has a qty (e.g. from the weight popup), pre-populate
    // the QTY field so the user doesn't have to re-enter it.
    const qtyField = (cfg.fields || []).find((f) => f.type === "QTY");
    if (qtyField) initial[qtyField.name] = productQty;
    setValues(initial);
    setTouched({});
    setSaveAttempted(false);
    setActiveSection(cfg.sections?.[0]?.id || null);
    setRecordingField(null);
    setWeightValue(null);
    setWeightTouched(false);
  }, [isOpen, selectedProduct, cfg, productQty]);

  const pricing = useMemo(
    () => computePricing(cfg, values, basePrice, isManualQtyMode ? (weightValue || 0) : productQty),
    [cfg, values, basePrice, isManualQtyMode, weightValue, productQty]
  );
  const weightValid = !isManualQtyMode || (weightValue !== null && weightValue > 0);
  const canSave = isAllValid(cfg, values) && weightValid;
  const singleSection = (cfg.sections?.length || 0) <= 1;

  const setField = (name, v) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  };
  const toggleMulti = (name, code, max) => {
    setValues((prev) => {
      const arr = Array.isArray(prev[name]) ? [...prev[name]] : [];
      const idx = arr.indexOf(code);
      if (idx >= 0) arr.splice(idx, 1);
      else if (arr.length < (max || 99)) arr.push(code);
      return { ...prev, [name]: arr };
    });
    setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`cad-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ───── S3 upload helpers ─────
  const compressImage = (file, maxWidth = 1920, quality = 0.75) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => { img.src = e.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error("Compression failed")); return; }
          resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
        }, file.type, quality);
      };
      img.onerror = reject;
      reader.onerror = reject;
    });

  const uploadToS3 = async (file) => {
    const toUpload = file.size > 4 * 1024 * 1024 && file.type.startsWith("image/")
      ? await compressImage(file)
      : file;
    const buffer = await toUpload.arrayBuffer();
    const fileData = Array.from(new Uint8Array(buffer));
    const resp = await fetch(env.uploadDocumentUrl, {
      method: "POST",
      headers: uploadDocumentHeaders(),
      body: JSON.stringify({ fileName: toUpload.name, fileType: toUpload.type, fileData }),
    });
    const result = await resp.json();
    if (!resp.ok) throw new Error(result?.error || "Upload failed");
    return result.url;
  };

  // ───── Upload helpers (UPL / IMG / MIM) ─────
  const handleFilesAdded = (fieldName, fileList, max) => {
    const files = Array.from(fileList || []);
    const slot = files.slice(0, max || 10);
    // 1. Add entries immediately with dataUrl preview + uploading flag
    const reads = slot.map(
      (f) =>
        new Promise((res) => {
          const r = new FileReader();
          r.onload = (e) =>
            res({ id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: f.name, size: f.size, type: f.type, dataUrl: e.target.result, uploading: true, url: null });
          r.readAsDataURL(f);
        })
    );
    Promise.all(reads).then((previews) => {
      setValues((prev) => {
        const cur = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
        return { ...prev, [fieldName]: [...cur, ...previews].slice(0, max || 10) };
      });
      // 2. Upload each to S3 and patch in the url
      previews.forEach((entry, i) => {
        uploadToS3(slot[i])
          .then((url) => {
            setValues((prev) => ({
              ...prev,
              [fieldName]: (prev[fieldName] || []).map((u) =>
                u.id === entry.id ? { ...u, url, uploading: false } : u
              ),
            }));
          })
          .catch(() => {
            message.error(`Failed to upload ${entry.name}`);
            setValues((prev) => ({
              ...prev,
              [fieldName]: (prev[fieldName] || []).map((u) =>
                u.id === entry.id ? { ...u, uploading: false, error: true } : u
              ),
            }));
          });
      });
    });
  };

  const removeUpload = (fieldName, id) =>
    setValues((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((u) => u.id !== id),
    }));

  // ───── Audio helpers (AUD) ─────
  const startRecording = async (fieldName, maxSec) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      message.error("Microphone not available on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const r = new FileReader();
        r.onload = (e) => {
          const duration = Math.round((Date.now() - recStartRef.current) / 1000);
          setField(fieldName, { dataUrl: e.target.result, duration, type: "audio/webm" });
        };
        r.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = rec;
      recStartRef.current = Date.now();
      rec.start();
      setRecordingField(fieldName);
      // auto-stop on max duration
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") stopRecording();
      }, (maxSec || 120) * 1000);
    } catch (err) {
      message.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecordingField(null);
  };

  // ───── Render per field ─────
  const renderField = (f) => {
    const val = values[f.name];
    const check = isFieldValid(f, val);

    let widget;
    switch (f.type) {
      case "STR":
        widget = (
          <Input
            className="cad-input"
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            value={val || ""}
            onChange={(e) => setField(f.name, e.target.value)}
          />
        );
        break;

      case "TXT":
        widget = (
          <TextArea
            rows={f.rows}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            value={val || ""}
            onChange={(e) => setField(f.name, e.target.value)}
          />
        );
        break;

      case "NUM":
        widget = (
          <InputNumber
            min={f.min ?? undefined}
            max={f.max ?? undefined}
            step={f.step || 1}
            precision={f.decimals || 0}
            value={val === "" ? null : val}
            onChange={(v) => setField(f.name, v)}
            addonAfter={f.unit || undefined}
            style={{ width: 160 }}
          />
        );
        break;

      case "QTY": {
        const n = Number(val) || f.min || 1;
        widget = (
          <div className="cad-qty">
            <button type="button" onClick={() => setField(f.name, Math.max(f.min || 1, n - (f.step || 1)))}>−</button>
            <input value={n} readOnly />
            <button type="button" onClick={() => setField(f.name, Math.min(f.max || 99, n + (f.step || 1)))}>+</button>
          </div>
        );
        break;
      }

      case "DAT": {
        const dayVal = val ? (dayjs.isDayjs(val) ? val : dayjs(val)) : null;
        const today = dayjs().startOf("day");
        const disabledDate = (cur) => {
          if (!cur) return false;
          if (f.minDate === "today" && cur < today) return true;
          if (f.minDate && f.minDate !== "today" && cur < dayjs(f.minDate)) return true;
          if (f.maxDate && cur > dayjs(f.maxDate)) return true;
          return false;
        };
        widget = (
          <DatePicker
            value={dayVal}
            format="DD-MM-YYYY"
            disabledDate={disabledDate}
            onChange={(d) => setField(f.name, d)}
            style={{ width: 200 }}
          />
        );
        break;
      }

      case "DTT": {
        const dayVal = val ? (dayjs.isDayjs(val) ? val : dayjs(val)) : null;
        widget = (
          <DatePicker
            showTime={{ format: "hh:mm A", minuteStep: f.dateStep || 15 }}
            value={dayVal}
            format="DD-MM-YYYY hh:mm A"
            onChange={(d) => setField(f.name, d)}
            onOk={(d) => setField(f.name, d)}
            style={{ width: 260 }}
          />
        );
        break;
      }

      case "YEN":
        widget = (
          <Radio.Group value={val} onChange={(e) => setField(f.name, e.target.value)}>
            <Radio value="Y">Yes</Radio>
            <Radio value="N">No</Radio>
          </Radio.Group>
        );
        break;

      case "LST":
        widget = (
          <Select
            value={val || undefined}
            onChange={(v) => setField(f.name, v)}
            style={{ width: 280 }}
            placeholder={f.placeholder || "Select"}
            options={(f.options || []).map((o) => ({ value: o.value, label: o.label }))}
          />
        );
        break;

      case "TGL":
        widget = (
          <div className="cad-toggle" role="group">
            {(f.options || []).map((o) => (
              <div
                key={o.value}
                className={"cad-toggle-opt" + (val === o.value ? " selected" : "")}
                onClick={() => setField(f.name, o.value)}
              >
                {o.label}
                {o.priceDelta ? (
                  <span className="cad-delta">
                    +{getCurrencySymbol(useAuthStore.getState().tillData)}
                    {o.priceDelta}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        );
        break;

      case "CHP":
        widget = (
          <div className="cad-chips">
            {(f.options || []).map((o) => {
              const sel = val === o.value;
              return (
                <button
                  type="button"
                  key={o.value}
                  className={"cad-chip" + (sel ? " selected" : "")}
                  onClick={() => setField(f.name, o.value)}
                >
                  {o.label}
                  {o.priceDelta ? (
                  <span className="cad-delta">
                    +{getCurrencySymbol(useAuthStore.getState().tillData)}
                    {o.priceDelta}
                  </span>
                ) : null}
                  {o.priceMultiplier && o.priceMultiplier !== 1 ? (
                    <span className="cad-delta">{o.priceMultiplier}×</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        );
        break;

      case "MCH":
        widget = (
          <div className="cad-chips">
            {(f.options || []).map((o) => {
              const sel = (val || []).includes(o.value);
              return (
                <button
                  type="button"
                  key={o.value}
                  className={"cad-chip" + (sel ? " selected" : "")}
                  onClick={() => toggleMulti(f.name, o.value, f.maxSelect)}
                >
                  {o.label}
                  {o.priceDelta ? (
                  <span className="cad-delta">
                    +{getCurrencySymbol(useAuthStore.getState().tillData)}
                    {o.priceDelta}
                  </span>
                ) : null}
                </button>
              );
            })}
          </div>
        );
        break;

      case "RDC": {
        const cols = f.columns || 4;
        widget = (
          <div className="cad-radio-cards" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {(f.options || []).map((o) => {
              const sel = val === o.value;
              return (
                <div
                  key={o.value}
                  className={"cad-radio-card" + (sel ? " selected" : "")}
                  onClick={() => setField(f.name, o.value)}
                >
                  <div className="cad-icon">
                    {o.image ? <img src={o.image} alt={o.label} style={{ maxHeight: 36 }} /> : o.icon || "◻"}
                  </div>
                  <div className="cad-name">{o.label}</div>
                  {o.priceDelta ? (
                    <div className="cad-delta">
                      {o.priceDelta > 0 ? "+" : ""}
                      {getCurrencySymbol(useAuthStore.getState().tillData)}
                      {o.priceDelta}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        );
        break;
      }

      case "GAL": {
        const items = f.galleryItems || []; // backend later supplies via source
        if (!items.length) {
          widget = (
            <div style={{ color: "#8c92ac", fontSize: 13 }}>
              Album <code>{f.source || "(none)"}</code> has no items configured.
            </div>
          );
          break;
        }
        widget = (
          <div className="cad-gallery">
            {items.map((it) => {
              const sel = f.multi ? (val || []).includes(it.id) : val === it.id;
              return (
                <div
                  key={it.id}
                  className={"cad-gallery-tile" + (sel ? " selected" : "")}
                  onClick={() =>
                    f.multi
                      ? toggleMulti(f.name, it.id, f.maxSelect)
                      : setField(f.name, it.id)
                  }
                >
                  {it.image ? <img src={it.image} alt={it.label} /> : <CameraOutlined style={{ fontSize: 32, color: "#8c92ac" }} />}
                  <div className="cad-gallery-badge">✓</div>
                  <div className="cad-gallery-label">{it.label || it.name}</div>
                </div>
              );
            })}
          </div>
        );
        break;
      }

      case "UPL": {
        const max = f.maxFiles || 5;
        widget = (
          <>
            <Upload.Dragger
              multiple={max > 1}
              accept={"image/*"}
              beforeUpload={() => false}
              showUploadList={false}
              onChange={(info) => {
                const files = info.fileList.map((x) => x.originFileObj).filter(Boolean);
                if (files.length) handleFilesAdded(f.name, files, max);
              }}
              className="cad-uploader"
            >
              <InboxOutlined style={{ fontSize: 26, color: "#8c92ac" }} />
              <div style={{ marginTop: 6 }}>
                Click or drag up to {max} image{max > 1 ? "s" : ""} here
              </div>
            </Upload.Dragger>
            {(val || []).length > 0 && (
              <div className="cad-upload-thumbs">
                {(val || []).map((u) => (
                  <div key={u.id} className="cad-upload-thumb">
                    {u.dataUrl ? <img src={u.dataUrl} alt={u.name} /> : "📷"}
                    <button type="button" className="cad-thumb-x" onClick={() => removeUpload(f.name, u.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </>
        );
        break;
      }

      case "IMG":
      case "MIM": {
        const max = f.type === "IMG" ? 1 : (f.maxFiles || 10);
        const uploads = Array.isArray(val) ? val : [];
        widget = (
          <div className="cad-mim-wrap">
            <div className="cad-mim-grid">
              {uploads.map((u) => (
                <div key={u.id} className={"cad-mim-tile" + (u.uploading ? " uploading" : u.error ? " error" : "")}>
                  <img src={u.dataUrl} alt={u.name} />
                  {u.uploading && <div className="cad-mim-spinner" />}
                  {!u.uploading && (
                    <button type="button" className="cad-mim-remove" onClick={() => removeUpload(f.name, u.id)}>✕</button>
                  )}
                </div>
              ))}
              {uploads.length < max && (
                <label className="cad-mim-add">
                  <input
                    type="file"
                    accept="image/*"
                    multiple={f.type !== "IMG"}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) handleFilesAdded(f.name, files, max);
                      e.target.value = "";
                    }}
                  />
                  <CameraOutlined style={{ fontSize: 22, color: "#8c92ac" }} />
                  <span style={{ fontSize: 12, color: "#8c92ac", marginTop: 4 }}>
                    {uploads.length > 0 ? "Add more" : "Add image" + (max > 1 ? "s" : "")}
                  </span>
                </label>
              )}
            </div>
            {uploads.length > 0 && (
              <div style={{ fontSize: 12, color: "#8c92ac", marginTop: 6 }}>
                {uploads.filter((u) => u.url).length} / {uploads.length} uploaded
              </div>
            )}
          </div>
        );
        break;
      }

      case "AUD": {
        const isRecording = recordingField === f.name;
        const hasAudio = val && val.dataUrl;
        widget = (
          <div className="cad-audio-recorder">
            <button
              type="button"
              className={"cad-audio-btn" + (isRecording ? " recording" : "")}
              onClick={() => {
                if (isRecording) stopRecording();
                else if (hasAudio) setField(f.name, null); // reset to re-record
                else startRecording(f.name, f.maxDurationSec);
              }}
              title={isRecording ? "Stop" : hasAudio ? "Re-record" : "Record"}
            >
              {isRecording ? "■" : hasAudio ? "↻" : <AudioOutlined />}
            </button>
            <div className={"cad-audio-wave" + (hasAudio ? " has-audio" : "")}>
              {hasAudio && (
                <audio controls src={val.dataUrl} style={{ width: "100%", height: 28 }} />
              )}
            </div>
            <div className="cad-audio-time">
              {isRecording ? "Recording…" : hasAudio ? `${val.duration || 0}s` : "Tap to record"}
            </div>
          </div>
        );
        break;
      }

      case "ADG": {
        const cols = f.columns || 3;
        widget = (
          <div className="cad-addon-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {(f.items || []).map((it) => {
              const sel = (val || []).includes(it.id);
              return (
                <div
                  key={it.id}
                  className={"cad-addon" + (sel ? " selected" : "")}
                  onClick={() => toggleMulti(f.name, it.id, f.maxSelect)}
                >
                  <div className="cad-addon-check">{sel ? "✓" : ""}</div>
                  <div className="cad-addon-name">{it.name}</div>
                  {it.description && <div className="cad-addon-desc">{it.description}</div>}
                  <div className="cad-addon-price">+{formatINR(it.price)}</div>
                </div>
              );
            })}
          </div>
        );
        break;
      }

      default:
        widget = <em>Unsupported field type: {f.type}</em>;
    }

    return (
      <div className="cad-field" key={f.name}>
        <div className="cad-field-label">
          {f.name}
          {f.mandatory === "Y" && <span className="cad-req">*</span>}
          {f.hint && <span className="cad-hint">— {f.hint}</span>}
        </div>
        {widget}
        {(touched[f.name] || saveAttempted) && !check.ok && check.msg && (
          <div className="cad-field-error">{check.msg}</div>
        )}
      </div>
    );
  };

  // ───── Summary ─────
  const renderSelections = () => {
    const rows = [];
    (cfg.fields || []).forEach((f) => {
      const v = values[f.name];
      let display = "";
      if (f.type === "MCH") {
        if (Array.isArray(v) && v.length) {
          const labels = v.map(
            (code) => (f.options || []).find((o) => o.value === code)?.label || code
          );
          display = labels.join(", ");
        }
      } else if (f.type === "ADG") {
        if (Array.isArray(v) && v.length) {
          display = `${v.length} add-on${v.length > 1 ? "s" : ""}`;
        }
      } else if (f.type === "UPL" || f.type === "IMG" || f.type === "MIM") {
        if (Array.isArray(v) && v.length) display = `${v.length} image${v.length > 1 ? "s" : ""}`;
      } else if (f.type === "GAL") {
        if (f.multi && Array.isArray(v) && v.length) display = `${v.length} selected`;
        else if (!f.multi && v) {
          display = (f.galleryItems || []).find((x) => x.id === v)?.label || v;
        }
      } else if (f.type === "AUD") {
        if (v && v.dataUrl) display = `🎙 ${v.duration || 0}s voice note`;
      } else if (f.type === "DAT" || f.type === "DTT") {
        if (v) display = (dayjs.isDayjs(v) ? v : dayjs(v)).format(f.type === "DTT" ? "DD-MM-YYYY hh:mm A" : "DD-MM-YYYY");
      } else if (["CHP", "TGL", "RDC", "LST"].includes(f.type)) {
        if (v) display = (f.options || []).find((o) => o.value === v)?.label || v;
      } else if (v !== "" && v !== null && v !== undefined) {
        display = String(v);
      }
      if (display) rows.push({ k: f.name, v: display });
    });
    if (!rows.length)
      return (
        <div className="cad-sel-row empty">
          <span className="cad-sel-v">No selections yet</span>
        </div>
      );
    return rows.map((r, i) => (
      <div key={i} className="cad-sel-row">
        <span className="cad-sel-k">{r.k}</span>
        <span className="cad-sel-v">{r.v}</span>
      </div>
    ));
  };

  const handleSave = () => {
    setSaveAttempted(true);
    setWeightTouched(true);
    if (!canSave) {
      if (isManualQtyMode && !weightValid) {
        message.warning("Please enter a valid weight");
      } else {
        message.warning("Please complete all required fields");
      }
      return;
    }
    // Check if any image is still uploading to S3
    const stillUploading = (cfg.fields || []).some((f) => {
      if (!["IMG", "MIM", "UPL"].includes(f.type)) return false;
      return (values[f.name] || []).some((u) => u.uploading);
    });
    if (stillUploading) {
      message.warning("Please wait — images are still uploading");
      return;
    }
    const flat = {};
    (cfg.fields || []).forEach((f) => {
      let v = values[f.name];
      if (f.type === "DAT" || f.type === "DTT") {
        if (v && dayjs.isDayjs(v)) v = v.format(f.type === "DTT" ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY");
      } else if (f.type === "IMG") {
        // Single image — send the S3 URL string (or null)
        v = Array.isArray(v) && v.length ? (v[0].url || null) : null;
      } else if (f.type === "MIM" || f.type === "UPL") {
        // Multiple images — send array of S3 URL strings
        v = Array.isArray(v) ? v.map((u) => u.url).filter(Boolean) : [];
      }
      flat[f.name] = v;
    });
    const finalWeight = isManualQtyMode ? parseFloat(weightValue) : undefined;
    onSubmit({
      newCustomAttributes: [flat],
      pricing,
      qty: isManualQtyMode ? finalWeight : pricing.qty,
      perUnit: pricing.perUnit,
      values,
      ...(isManualQtyMode ? { weight: finalWeight } : {}),
    });
  };

  if (!selectedProduct) return null;

  return (
    <Drawer
      open={isOpen}
      onClose={onCancel}
      placement="right"
      width="85vw"
      closable={false}
      maskClosable={false}
      keyboard={false}
      styles={{ body: { padding: 0, background: "#f3f4f9" }, header: { display: "none" } }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e6e8f0",
        }}
      >
        <div>
          <span style={{ fontSize: 17, fontWeight: 600, color: "#2f3856" }}>
            Customise — {selectedProduct.name}
          </span>
          <span style={{ color: "#8c92ac", fontSize: 13, marginLeft: 8 }}>
            {singleSection ? "" : `· ${cfg.sections.length} sections `}· base {formatINR(basePrice)}
          </span>
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={onCancel} />
      </div>

      {/* Body */}
      <div
        className="cad-body"
        style={{
          height: "calc(100vh - 58px - 70px)",
          gridTemplateColumns: singleSection ? "1fr 360px" : "220px 1fr 360px",
        }}
      >
        {/* Left rail — hidden for single-section legacy configs */}
        {!singleSection && (
          <aside className="cad-rail">
            <div className="cad-group-label">Sections</div>
            {cfg.sections.map((s) => {
              const complete = isSectionComplete(cfg, s.id, values);
              const needsReq = sectionHasRequired(cfg, s.id) && !complete;
              return (
                <div
                  key={s.id}
                  className={
                    "cad-rail-item" +
                    (complete ? " complete" : "") +
                    (activeSection === s.id ? " active" : "")
                  }
                  onClick={() => scrollToSection(s.id)}
                >
                  <span className="cad-dot" />
                  <span>{s.title}</span>
                  {needsReq && <span className="cad-req-flag">required</span>}
                </div>
              );
            })}
          </aside>
        )}

        {/* Form */}
        <main className="cad-center">
          {/* Weight input — shown when product needs manual qty entry */}
          {isManualQtyMode && (
            <section className="cad-section cad-weight-section">
              <h3>Weight / Quantity</h3>
              <div className="cad-field">
                <div className="cad-field-label">
                  Weight <span className="cad-req">*</span>
                  <span className="cad-hint">— {weightUomName}</span>
                </div>
                <InputNumber
                  min={isDecimalQty ? Math.pow(10, -weightPrecision) : 1}
                  step={isDecimalQty ? Math.pow(10, -weightPrecision) : 1}
                  precision={weightPrecision}
                  value={weightValue}
                  onChange={(v) => { setWeightValue(v); setWeightTouched(true); }}
                  addonAfter={weightUomName}
                  style={{ width: 220 }}
                  placeholder={isDecimalQty ? (0).toFixed(weightPrecision) : "0"}
                  autoFocus
                />
                {(weightTouched || saveAttempted) && !weightValid && (
                  <div className="cad-field-error">Please enter a valid weight</div>
                )}
              </div>
            </section>
          )}
          {cfg.sections.map((s) => {
            const fields = fieldsBySection(cfg, s.id);
            if (!fields.length) return null;
            return (
              <section key={s.id} id={`cad-sec-${s.id}`} className="cad-section">
                {!singleSection && <h3>{s.title}</h3>}
                {s.description && <div className="cad-section-desc">{s.description}</div>}
                {fields.map(renderField)}
              </section>
            );
          })}
        </main>

        {/* Summary */}
        <aside className="cad-summary">
          <div className="cad-product">
            <div className="cad-prod-img">
              {selectedProduct.imageurl ? (
                <img src={selectedProduct.imageurl} alt={selectedProduct.name} />
              ) : (
                <img src={DefaultProductImage} alt="" />
              )}
            </div>
            <div className="cad-prod-meta">
              <div className="cad-prod-name">{selectedProduct.name}</div>
              <div className="cad-prod-base">Base {formatINR(basePrice)}</div>
            </div>
          </div>

          <h4>Your Selections</h4>
          {renderSelections()}

          <div className="cad-price-block">
            <div className="cad-price-row">
              <span>Base price</span>
              <span>{formatINR(basePrice * (pricing.qty || 1))}</span>
            </div>
            <div className="cad-price-row">
              <span>Customisations</span>
              <span>{formatINR(pricing.customisationDelta * (pricing.qty || 1))}</span>
            </div>
            <div className="cad-price-row">
              <span>Add-ons</span>
              <span>{formatINR(pricing.addonTotal * (pricing.qty || 1))}</span>
            </div>
            <div className="cad-price-row total">
              <span>Total</span>
              <span>{formatINR(pricing.lineTotal)}</span>
            </div>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <div className="cad-footer">
        <div className="cad-hint">
          {canSave
            ? "All required fields complete — ready to add"
            : "Fill all required sections to continue"}
        </div>
        <div>
          <Button onClick={onCancel} style={{ marginRight: 10 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canSave}
            onClick={handleSave}
            style={{ minWidth: 240, background: canSave ? "#2f3856" : undefined }}
          >
            Save &amp; Add to Cart · {formatINR(pricing.lineTotal)}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default CustomAttributesDrawer;
